import type { ColumnDef } from "@tanstack/react-table";
import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import { format } from "date-fns";
import type { Recipe } from "@/types/recipe";

/**
 * Column definitions used exclusively for Excel/CSV export.
 * Work directly on the raw Recipe API type — no mapping step — so every
 * importable field is available and round-trips cleanly.
 *
 * meta.label   → Excel column header
 * accessorKey  → raw field value
 * accessorFn   → computed/formatted value (status, dates, allergens …)
 */
export function getMenuItemExportColumns(i18n: I18n): ColumnDef<Recipe, unknown>[] {
  return [
    {
      id: "id",
      accessorKey: "id",
      meta: { label: i18n._(msg`ID`) },
    },
    {
      id: "name",
      accessorKey: "name",
      meta: { label: i18n._(msg`Name`) },
    },
    {
      id: "slug",
      accessorKey: "slug",
      meta: { label: i18n._(msg`Recipe Code`) },
    },
    {
      id: "category",
      accessorKey: "category",
      meta: { label: i18n._(msg`Category`) },
    },
    {
      id: "subcategory",
      accessorKey: "subcategory",
      meta: { label: i18n._(msg`Subcategory`) },
    },
    {
      id: "description",
      accessorKey: "description",
      meta: { label: i18n._(msg`Description`) },
    },
    {
      id: "difficulty",
      accessorKey: "difficulty",
      meta: { label: i18n._(msg`Difficulty`) },
    },
    {
      id: "prepTimeMinutes",
      accessorKey: "prepTimeMinutes",
      meta: { label: i18n._(msg`Prep Time (min)`) },
    },
    {
      id: "cookTimeMinutes",
      accessorKey: "cookTimeMinutes",
      meta: { label: i18n._(msg`Cook Time (min)`) },
    },
    {
      id: "restTimeMinutes",
      accessorKey: "restTimeMinutes",
      meta: { label: i18n._(msg`Rest Time (min)`) },
    },
    {
      id: "servings",
      accessorKey: "servings",
      meta: { label: i18n._(msg`Servings`) },
    },
    {
      id: "servingSize",
      accessorKey: "servingSize",
      meta: { label: i18n._(msg`Serving Size`) },
    },
    {
      id: "tips",
      accessorKey: "tips",
      meta: { label: i18n._(msg`Tips`) },
    },
    {
      id: "labelingRequirements",
      accessorKey: "labelingRequirements",
      meta: { label: i18n._(msg`Labeling Requirements`) },
    },
    {
      id: "targetSellingPrice",
      accessorKey: "targetSellingPrice",
      meta: { label: i18n._(msg`Target Selling Price`) },
    },
    {
      id: "status",
      accessorFn: (row) =>
        !row.isActive
          ? i18n._(msg`Archived`)
          : row.isPublished
            ? i18n._(msg`Published`)
            : i18n._(msg`Draft`),
      meta: { label: i18n._(msg`Status`) },
    },
    {
      id: "allergens",
      accessorFn: (row) =>
        (row.allergens ?? []).map((a) => a.code).join(", "),
      meta: { label: i18n._(msg`Allergens`) },
    },
    {
      id: "targets",
      accessorFn: (row) => {
        const hasRegionScope =
          row.scopes?.some(
            (s) => s.scopeType === "REGION" && !s.isExcluded,
          ) ?? false;
        return hasRegionScope ? i18n._(msg`Region share`) : i18n._(msg`Org-wide`);
      },
      meta: { label: i18n._(msg`Targets`) },
    },
    {
      id: "version",
      accessorKey: "version",
      meta: { label: i18n._(msg`Version`) },
    },
    {
      id: "createdAt",
      accessorFn: (row) => format(new Date(row.createdAt), "yyyy-MM-dd HH:mm"),
      meta: { label: i18n._(msg`Created At`) },
    },
    {
      id: "updatedAt",
      accessorFn: (row) => format(new Date(row.updatedAt), "yyyy-MM-dd HH:mm"),
      meta: { label: i18n._(msg`Updated At`) },
    },
  ];
}
